/**
 * Екран „Настройки“ (Раздел 8): срокове за съхранение, праг за проблемен
 * разговор, получатели на известия и таблица с вътрешните номера.
 */

import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Group,
  NumberInput,
  Stack,
  Switch,
  Table,
  TagsInput,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../api/client';
import { validateExtensionDraft } from '../lib/extension-draft';
import type { ExtensionItem, SettingsResponse } from '../api/types';

export function SettingsPage() {
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiRequest<SettingsResponse>('/api/settings'),
  });

  const extensions = useQuery({
    queryKey: ['extensions'],
    queryFn: () => apiRequest<{ items: ExtensionItem[] }>('/api/extensions'),
  });

  const [threshold, setThreshold] = useState(60);
  const [audioDays, setAudioDays] = useState(180);
  const [dataDays, setDataDays] = useState(365);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [rows, setRows] = useState<ExtensionItem[]>([]);
  const [draft, setDraft] = useState({ extension: '', employeeName: '', groupName: '' });
  const [draftError, setDraftError] = useState<string | null>(null);
  // Още незаписаните редове могат да се премахнат; записаните — не, защото
  // разговорите сочат към тях.
  const [unsavedIds, setUnsavedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!settings.data) return;
    setThreshold(settings.data.flagThreshold.value);
    setAudioDays(settings.data.retention.audioDays);
    setDataDays(settings.data.retention.dataDays);
    setRecipients(settings.data.notifications.failureRecipients);
  }, [settings.data]);

  useEffect(() => {
    if (extensions.data) {
      setRows(extensions.data.items);
      setUnsavedIds([]);
    }
  }, [extensions.data]);

  const saveSetting = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      apiRequest(`/api/settings/${key}`, { method: 'PUT', body: value }),
    onSuccess: async () => {
      notifications.show({ message: 'Настройката е записана', color: 'green' });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (error: Error) => notifications.show({ message: error.message, color: 'red' }),
  });

  const saveExtensions = useMutation({
    mutationFn: () =>
      apiRequest('/api/extensions', {
        method: 'POST',
        body: rows.map((row) => ({
          extension: row.extension,
          employeeName: row.employeeName,
          groupName: row.groupName,
          isActive: row.isActive,
        })),
      }),
    onSuccess: async () => {
      notifications.show({ message: 'Вътрешните номера са записани', color: 'green' });
      await queryClient.invalidateQueries({ queryKey: ['extensions'] });
    },
    onError: (error: Error) => notifications.show({ message: error.message, color: 'red' }),
  });

  const updateRow = (id: string, patch: Partial<ExtensionItem>): void => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  /** Добавя ред към таблицата; записва се заедно с останалите промени. */
  const addDraft = (): void => {
    const problem = validateExtensionDraft(draft, rows.map((row) => row.extension));
    if (problem !== null) {
      setDraftError(problem);
      return;
    }

    // Идентификаторът е временен: сървърът разпознава реда по самия номер.
    const id = crypto.randomUUID();
    setRows((current) => [
      ...current,
      {
        id,
        extension: draft.extension.trim(),
        employeeName: draft.employeeName.trim(),
        groupName: draft.groupName.trim() || null,
        isActive: true,
      },
    ]);
    setUnsavedIds((current) => [...current, id]);
    setDraft({ extension: '', employeeName: '', groupName: '' });
    setDraftError(null);
  };

  const removeDraftRow = (id: string): void => {
    setRows((current) => current.filter((row) => row.id !== id));
    setUnsavedIds((current) => current.filter((candidate) => candidate !== id));
  };

  return (
    <Stack>
      <Title order={3}>Настройки</Title>

      <Card withBorder>
        <Title order={5} mb="sm">
          Праг за проблемен разговор
        </Title>
        <Group align="end">
          <NumberInput
            label="Обща оценка под"
            description="Разговор под този праг се маркира като проблемен"
            value={threshold}
            onChange={(value) => setThreshold(Number(value))}
            min={0}
            max={100}
            w={220}
          />
          <Button
            onClick={() => saveSetting.mutate({ key: 'flag_threshold', value: { value: threshold } })}
            loading={saveSetting.isPending}
          >
            Запиши
          </Button>
        </Group>
      </Card>

      <Card withBorder>
        <Title order={5} mb="sm">
          Срокове за съхранение
        </Title>
        <Text size="sm" c="dimmed" mb="sm">
          След изтичане на срока данните се изтриват необратимо, а действието се вписва в журнала.
        </Text>
        <Group align="end">
          <NumberInput
            label="Аудио (дни)"
            value={audioDays}
            onChange={(value) => setAudioDays(Number(value))}
            min={1}
            w={200}
          />
          <NumberInput
            label="Транскрипции и оценки (дни)"
            value={dataDays}
            onChange={(value) => setDataDays(Number(value))}
            min={1}
            w={260}
          />
          <Button
            onClick={() => saveSetting.mutate({ key: 'retention', value: { audioDays, dataDays } })}
            loading={saveSetting.isPending}
          >
            Запиши
          </Button>
        </Group>
      </Card>

      <Card withBorder>
        <Title order={5} mb="sm">
          Получатели на известия при грешка
        </Title>
        <Group align="end">
          <TagsInput
            label="Имейл адреси"
            description="Натиснете Enter след всеки адрес"
            value={recipients}
            onChange={setRecipients}
            w={480}
          />
          <Button
            onClick={() =>
              saveSetting.mutate({
                key: 'notifications',
                value: { failureRecipients: recipients },
              })
            }
            loading={saveSetting.isPending}
          >
            Запиши
          </Button>
        </Group>
      </Card>

      <Card withBorder>
        <Group justify="space-between" mb="sm">
          <Title order={5}>Вътрешни номера</Title>
          <Button onClick={() => saveExtensions.mutate()} loading={saveExtensions.isPending}>
            Запиши промените
          </Button>
        </Group>
        <Text size="sm" c="dimmed" mb="sm">
          Записи от номер, който не е в този списък, се отхвърлят при качването.
        </Text>

        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={120}>Вътрешен номер</Table.Th>
              <Table.Th>Служител</Table.Th>
              <Table.Th w={220}>Група</Table.Th>
              <Table.Th w={100}>Активен</Table.Th>
              <Table.Th w={60} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td>{row.extension}</Table.Td>
                <Table.Td>
                  <TextInput
                    value={row.employeeName}
                    onChange={(event) =>
                      updateRow(row.id, { employeeName: event.currentTarget.value })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    value={row.groupName ?? ''}
                    onChange={(event) =>
                      updateRow(row.id, { groupName: event.currentTarget.value || null })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <Switch
                    checked={row.isActive}
                    onChange={(event) =>
                      updateRow(row.id, { isActive: event.currentTarget.checked })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  {unsavedIds.includes(row.id) && (
                    <Button
                      variant="subtle"
                      color="red"
                      size="compact-sm"
                      onClick={() => removeDraftRow(row.id)}
                    >
                      Премахни
                    </Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Group align="end" mt="md">
          <TextInput
            label="Нов вътрешен номер"
            placeholder="42"
            value={draft.extension}
            onChange={(event) => setDraft({ ...draft, extension: event.currentTarget.value })}
            error={draftError !== null}
            w={160}
          />
          <TextInput
            label="Служител"
            value={draft.employeeName}
            onChange={(event) => setDraft({ ...draft, employeeName: event.currentTarget.value })}
            error={draftError !== null}
            w={260}
          />
          <TextInput
            label="Група"
            value={draft.groupName}
            onChange={(event) => setDraft({ ...draft, groupName: event.currentTarget.value })}
            w={220}
          />
          <Button variant="default" onClick={addDraft}>
            Добави
          </Button>
        </Group>

        {draftError !== null && (
          <Text c="red" size="sm" mt="xs">
            {draftError}
          </Text>
        )}

        <Text size="sm" c="dimmed" mt="xs">
          Новите редове влизат в базата чак след „Запиши промените“. Вече записан
          номер не се трие — изключва се с превключвателя, защото обработените
          разговори сочат към него.
        </Text>
      </Card>
    </Stack>
  );
}
